import type { MouseEvent, ReactNode } from "react";
import { Link } from "react-router";

import type { TicketSummary } from "../api/types";
import { formatDate, formatDateTime } from "../lib/format";
import { Badge } from "../ui/Badge";
import { TYPE_LABELS } from "./labels";
import { PriorityBadge, RestrictedBadge, StatusBadge } from "./TicketBadges";

/** A small filter button beside each column heading (both ticket tables). */
export interface ColumnFilters {
  ticket?: ReactNode;
  type?: ReactNode;
  raised?: ReactNode;
  updated?: ReactNode;
  status?: ReactNode;
  priority?: ReactNode;
  service?: ReactNode;
  group?: ReactNode;
  owner?: ReactNode;
  requester?: ReactNode;
  due?: ReactNode;
}

interface TicketTableProps {
  tickets: TicketSummary[];
  /** Staff columns: group, owner and requester. */
  staff?: boolean;
  /** Every column (the admin overview): staff columns plus type, raised and updated. */
  full?: boolean;
  /** Filters on the headings. Service, Group and Owner then get a column each. */
  filters?: ColumnFilters;
  /** Bulk selection (workspace). */
  selected?: ReadonlySet<string>;
  onToggle?: (reference: string, checked: boolean) => void;
  onToggleAll?: (checked: boolean) => void;
  /** Split view (DECISIONS #53): open a ticket beside the list. Returns false to follow the link. */
  onOpen?: (reference: string) => boolean;
  /** The ticket open beside the list, and the row the keyboard is on. */
  openRef?: string | null;
  activeRef?: string | null;
  /** A ticket is open beside the list: just the ticket, status, priority and owner. */
  compact?: boolean;
}

/** A plain click opens beside the list; Ctrl/Cmd/Shift-click still opens a new tab or window. */
function plainClick(event: MouseEvent): boolean {
  return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
}

function Th({ label, filter }: { label: string; filter?: ReactNode }) {
  return (
    <th role="columnheader" scope="col">
      {filter ? (
        <span className="th-inner">
          {label}
          {filter}
        </span>
      ) : (
        label
      )}
    </th>
  );
}

const done = (ticket: TicketSummary) =>
  ticket.status === "resolved" || ticket.status === "closed";

export function TicketTable({
  tickets,
  staff: staffProp = false,
  full = false,
  filters,
  selected,
  onToggle,
  onToggleAll,
  onOpen,
  openRef,
  activeRef,
  compact = false,
}: TicketTableProps) {
  // Compact: the other columns' filters still apply, their columns are just out of the way.
  const split = Boolean(filters) && !compact;
  const staff = (staffProp || full || Boolean(filters)) && !compact;
  full = full && !compact;
  const selectable = Boolean(selected && onToggle);
  const allSelected =
    selectable &&
    tickets.length > 0 &&
    tickets.every((t) => selected?.has(t.reference));

  return (
    <table
      className={[
        "table ticket-table",
        filters ? "ticket-table-filters" : "",
        compact ? "ticket-table-compact" : "",
        onOpen ? "ticket-table-openable" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      role="table"
    >
      <thead role="rowgroup">
        <tr role="row">
          {selectable ? (
            <th role="columnheader" scope="col" className="cell-check">
              <input
                type="checkbox"
                aria-label="Select all tickets on this page"
                checked={allSelected}
                onChange={(event) => onToggleAll?.(event.target.checked)}
              />
            </th>
          ) : null}
          <Th label="Ticket" filter={filters?.ticket} />
          {full ? <Th label="Type" filter={filters?.type} /> : null}
          <Th label="Status" filter={filters?.status} />
          <Th label="Priority" filter={filters?.priority} />
          {split ? <Th label="Service" filter={filters?.service} /> : null}
          {split ? (
            <>
              <Th label="Group" filter={filters?.group} />
              <Th label="Owner" filter={filters?.owner} />
            </>
          ) : staff ? (
            <Th label="Group / owner" />
          ) : null}
          {staff ? <Th label="Requester" filter={filters?.requester} /> : null}
          {full ? (
            <>
              <Th label="Raised" filter={filters?.raised} />
              <Th label="Updated" filter={filters?.updated} />
            </>
          ) : null}
          {compact ? <Th label="Owner" filter={filters?.owner} /> : null}
          {compact ? null : <Th label={staff ? "Due" : "Updated"} filter={filters?.due} />}
        </tr>
      </thead>
      <tbody role="rowgroup">
        {tickets.map((ticket) => (
          <tr
            key={ticket.id}
            role="row"
            data-ticket-row={ticket.reference}
            className={
              ticket.reference === openRef
                ? "row-open"
                : ticket.reference === activeRef
                  ? "row-active"
                  : undefined
            }
            aria-current={ticket.reference === openRef ? "true" : undefined}
            onClick={(event) => {
              // The whole row opens it beside the list; links, buttons and checkboxes do their own thing.
              if (!onOpen || !plainClick(event)) return;
              if ((event.target as HTMLElement).closest("a, button, input, label")) return;
              onOpen(ticket.reference);
            }}
          >
            {selectable ? (
              <td role="cell" className="cell-check">
                <input
                  type="checkbox"
                  aria-label={`Select ${ticket.reference}`}
                  checked={selected?.has(ticket.reference) ?? false}
                  onChange={(event) =>
                    onToggle?.(ticket.reference, event.target.checked)
                  }
                />
              </td>
            ) : null}
            <td role="cell" className="cell-lead">
              {/* The reference is the heading; the title says what it's about. */}
              <Link
                to={`/tickets/${ticket.reference}`}
                className="ticket-link"
                aria-label={`${ticket.reference}: ${ticket.title}`}
                onClick={(event) => {
                  if (onOpen && plainClick(event) && onOpen(ticket.reference)) event.preventDefault();
                }}
              >
                <span className="cell-strong ticket-ref">{ticket.reference}</span>
              </Link>
              <span className="ticket-row-title">{ticket.title}</span>
              {ticket.service && !split ? (
                <span className="meta-row">
                  <span>{ticket.service.name}</span>
                </span>
              ) : null}
              {ticket.is_restricted || ticket.tags.length ? (
                <span className="badges ticket-row-badges">
                  {ticket.is_restricted ? <RestrictedBadge /> : null}
                  {ticket.tags.map((tag) => (
                    <Badge key={tag}>#{tag}</Badge>
                  ))}
                </span>
              ) : null}
            </td>
            {full ? (
              <td role="cell" className="cell-indent">
                <span className="cell-label">Type: </span>
                {TYPE_LABELS[ticket.type]}
              </td>
            ) : null}
            <td role="cell" className="cell-trail">
              <StatusBadge status={ticket.status} />
            </td>
            <td role="cell" className="cell-indent">
              <PriorityBadge priority={ticket.priority} />
            </td>
            {split ? (
              <>
                <td role="cell" className="cell-indent">
                  <span className="cell-label">Service: </span>
                  {ticket.service?.name ?? "—"}
                </td>
                <td role="cell" className="cell-indent">
                  <span className="cell-label">Group: </span>
                  {ticket.group?.name ?? "No group"}
                </td>
                <td role="cell" className="cell-indent">
                  <span className="cell-label">Owner: </span>
                  {ticket.owner?.name ?? "Unassigned"}
                </td>
              </>
            ) : staff ? (
              <td role="cell" className="cell-indent">
                <span className="cell-label">Group: </span>
                {ticket.group?.name ?? "No group"}
                <span className="cell-secondary">
                  {ticket.owner ? ticket.owner.name : "Unassigned"}
                </span>
              </td>
            ) : null}
            {staff ? (
              <td role="cell" className="cell-indent">
                <span className="cell-label">Requester: </span>
                {ticket.requester.name}
              </td>
            ) : null}
            {full ? (
              <>
                <td role="cell" className="cell-indent cell-date">
                  <span className="cell-label">Raised: </span>
                  {formatDate(ticket.created_at)}
                </td>
                <td role="cell" className="cell-indent cell-date">
                  <span className="cell-label">Updated: </span>
                  {formatDateTime(ticket.last_activity_at)}
                </td>
              </>
            ) : null}
            {compact ? (
              <td role="cell" className="cell-indent">
                <span className="cell-label">Owner: </span>
                {ticket.owner?.name ?? ticket.group?.name ?? "Unassigned"}
              </td>
            ) : null}
            {compact ? null : (
              <td
                role="cell"
                className={
                  full || split ? "cell-indent cell-date" : "cell-indent"
                }
              >
                {full || split ? (
                  // Narrower than the sentence below: the time, with "Overdue" under it.
                  <>
                    <span className="cell-label">Due: </span>
                    {done(ticket) ? (
                      "—"
                    ) : (
                      <span
                        className={ticket.overdue ? "due-overdue" : undefined}
                      >
                        {formatDateTime(ticket.due_at)}
                      </span>
                    )}
                    {ticket.overdue ? (
                      <span className="cell-secondary due-overdue">Overdue</span>
                    ) : null}
                  </>
                ) : staff ? (
                  <span className={ticket.overdue ? "due-overdue" : undefined}>
                    <span className="cell-label">Due: </span>
                    {ticket.overdue ? "Overdue since " : ""}
                    {done(ticket) ? "—" : formatDateTime(ticket.due_at)}
                  </span>
                ) : (
                  <>
                    <span className="cell-label">Updated: </span>
                    {formatDateTime(ticket.last_activity_at)}
                  </>
                )}
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
